import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { ApolloError, useMutation } from '@apollo/client'
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { observer } from 'mobx-react'

import { Alert, ButtonGroup, CardBody } from 'Components/_theme'
import { Form } from 'Components/Form/Form'
import { FormOptionalMessage } from 'Components/FormOptionalMessage/FormOptionalMessage'
import { ModalDevicePresentationBlock } from 'Components/ModalDevicePresentationBlock/ModalDevicePresentationBlock'
import { ModalErrorState } from 'Components/ModalErrorState/ModalErrorState'

import { useStore } from 'Stores/index'

import {
  getSelectValue,
  parseShippingAddressBeforeSubmit,
  setFormErrors,
} from 'Utilities'

import { countryISOCodes } from 'Constants/constants'
import {
  E2E_LOST_STOLEN_BUTTON_BACK,
  E2E_LOST_STOLEN_BUTTON_SUBMIT,
  E2E_LOST_STOLEN_SUMMARY_MESSAGE,
} from 'Constants/e2e'
import {
  mutationLostDevice,
  mutationLostDeviceVariables,
} from 'Constants/graphql/mutations/__generated__/mutationLostDevice'
import { MUTATION_LOST_DEVICE } from 'Constants/graphql/mutations/MutationLostDevice'

import { ReplacementType } from 'Portal/__generated__/globalTypes'
import { useActiveUser } from 'Portal/src/hooks'

import { LOST_STOLEN_MODAL_SUMMARY_FORM } from '../../LostOrStolenModal.constants'
import { lostOrStolenModalSummarySchema } from '../../LostOrStolenModal.schema'
import {
  LostOrStolenModalStepsEnum,
  LostOrStolenModalSummaryProps,
  LostOrStolenModalSummarySchema,
} from '../../LostOrStolenModal.types'

export const LostOrStolenModalSummary = observer(
  (props: LostOrStolenModalSummaryProps) => {
    const {
      selectionType,
      rental,
      shippingFormData,
      replacementFormData,
      messageRef,
      requestIdRef,
      onSetCurrentStep,
      onSubmit,
    } = props
    const { userStore } = useStore()
    const { user } = userStore
    const { activeEmployee, activeOrganisation } = useActiveUser()
    const organisationID =
      activeEmployee?.organisation?.id || activeOrganisation!.id
    const intl = useIntl()
    const [submitError, setSubmitError] = useState<ApolloError | null>(null)
    const formMethods = useForm<LostOrStolenModalSummarySchema>({
      defaultValues: {
        message: '',
      },
      resolver: zodResolver(lostOrStolenModalSummarySchema),
    })
    const { getValues } = formMethods

    const [submitLostDevice, { loading: isLoading }] = useMutation<
      mutationLostDevice,
      mutationLostDeviceVariables
    >(MUTATION_LOST_DEVICE, {
      onCompleted: data => {
        if (data.createLostDeviceCaseItem.__typename === 'LostDeviceProblem') {
          setFormErrors<LostOrStolenModalSummarySchema>(
            data,
            'createLostDeviceCaseItem.invalidInputs',
            formMethods.setError,
          )

          return
        }

        messageRef.current = getValues().message
        requestIdRef.current = data.createLostDeviceCaseItem.result?.id!

        // refresh the rental
        onSubmit()

        onSetCurrentStep(LostOrStolenModalStepsEnum.CONFIRMATION)
      },
      onError: error => {
        setSubmitError(error)
      },
    })

    const handlePrev = () => {
      messageRef.current = null

      if (selectionType === ReplacementType.none) {
        onSetCurrentStep(LostOrStolenModalStepsEnum.LANDING)
      } else if (selectionType === ReplacementType.depot) {
        onSetCurrentStep(LostOrStolenModalStepsEnum.LANDING)
      } else {
        onSetCurrentStep(LostOrStolenModalStepsEnum.SHIPPING_FORM)
      }
    }

    const handleSubmit = formMethods.handleSubmit(data => {
      submitLostDevice({
        variables: {
          input: {
            message: data.message,
            organisationID,
            rentalID: rental.id,
            replacementType: selectionType,
            requestingUserID: user.userId?.toString() ?? '0',
            shippingAddress: parseShippingAddressBeforeSubmit(shippingFormData),
            stockListItemID:
              getSelectValue(replacementFormData?.nextDeviceId) || null,
          },
        },
      })
    })

    const hasError = !!submitError
    const isUnitedStatesAddressType =
      shippingFormData?.country?.value === countryISOCodes.US

    return (
      <CardBody>
        {!hasError ? (
          <>
            <Alert iconLeft={faCircleInfo} variant="info">
              <FormattedMessage id="LostOrStolenModal_followInternalProcesses" />
            </Alert>

            <ModalDevicePresentationBlock className="mt-6" rental={rental}>
              {shippingFormData && (
                <>
                  <h6>
                    <FormattedMessage id="Shipping_address" />
                  </h6>

                  <p>
                    {shippingFormData.name}
                    <br />
                    {shippingFormData.nameAddition}
                    <br />
                    {isUnitedStatesAddressType
                      ? shippingFormData.address
                      : `${shippingFormData.street} / ${shippingFormData.streetNumber}`}
                    <br />
                    {shippingFormData.zipcode} / {shippingFormData.city}
                    <br />
                    {shippingFormData.country?.label}
                  </p>
                </>
              )}
            </ModalDevicePresentationBlock>

            <div className="mt-6">
              <FormProvider {...formMethods}>
                <Form
                  id={LOST_STOLEN_MODAL_SUMMARY_FORM}
                  onSubmit={handleSubmit}
                >
                  <FormOptionalMessage
                    e2eSelector={E2E_LOST_STOLEN_SUMMARY_MESSAGE}
                  />
                </Form>
              </FormProvider>
            </div>
          </>
        ) : (
          <ModalErrorState error={submitError} />
        )}

        <ButtonGroup
          leftButton={{
            'data-e2e': E2E_LOST_STOLEN_BUTTON_BACK,
            onClick: handlePrev,
            text: intl.formatMessage({ id: 'Back' }),
          }}
          rightButton={
            !hasError
              ? {
                  'data-e2e': E2E_LOST_STOLEN_BUTTON_SUBMIT,
                  disabled: isLoading,
                  form: LOST_STOLEN_MODAL_SUMMARY_FORM,
                  text: intl.formatMessage({ id: 'Submit' }),
                  type: 'submit',
                }
              : undefined
          }
        />
      </CardBody>
    )
  },
)
